import {
	faFilter,
	faMagnifyingGlass,
	faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateSimple } from '@supply-mover-2-0/core';
import { Select, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useId, useState } from 'react';
import type {
	HistoryFilterChipModel,
	HistoryFilterField,
} from '../lib/historyFilters';
import {
	blankHistoryValues,
	clearedHistoryValues,
	countActiveFilters,
	DEFAULT_HISTORY_RANGE,
	HISTORY_SEARCH_CHIP_ID,
	historyFilterChips,
	historyFiltersDifferFromDefaults,
	historyRangeOptions,
	historyValuesWithoutChip,
} from '../lib/historyFilters';
import { AccountLocationPicker } from './AccountLocationPicker';

/**
 * Filtering for the history surfaces — Orders, Invoices, Statements,
 * Transactions (PD-14674, reworked in PD-14701).
 *
 * The search box (when a surface has one) stays on the page; every other
 * filter — relative date range, document type, payment status, location —
 * folds into ONE tray that opens from a filter button styled to match the
 * search box, and commits with explicit Apply / Cancel. The tray holds its
 * own DRAFT copy of the values, so nothing changes the list until Apply;
 * Cancel discards the draft.
 */

// The pure model (range presets, field shapes, active counts, chips) lives in
// lib/historyFilters.ts so it can be tested without React; the pages import
// it from here as they always have.
export {
	DEFAULT_HISTORY_RANGE,
	historyRangeLabel,
	historyRangeOptions,
	historyRangeParamUpdates,
	resolveHistoryRange,
} from '../lib/historyFilters';
export type {
	HistoryFilterField,
	HistoryRangeBounds,
} from '../lib/historyFilters';

interface HistoryFiltersProps {
	/** Filters for this surface, rendered top-to-bottom in the tray. */
	fields: Array<HistoryFilterField>;
	/** Committed values, keyed by field key ('range', 'type', ...). */
	values: Record<string, string>;
	/** Commit the tray's draft — the page maps these to its URL params. */
	onApply: (next: Record<string, string>) => void;
	/** Omit the search props to render the filter control alone. */
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	onSearchSubmit?: () => void;
	searchPlaceholder?: string;
	/**
	 * PD-17321 — show what is applied as removable chips beneath the row, and
	 * count the date range in the Filters badge even at its default. A chip's
	 * x and Clear all commit through `onApply`, as the tray's Apply does;
	 * clearing the search adds `q: ''` to that payload (absent otherwise), so a
	 * page with a search box maps `q` there too.
	 */
	showActiveFilters?: boolean;
	/** The committed search (the URL's, not what is being typed), shown as a chip. */
	appliedSearch?: string;
}

export function HistoryFilters({
	fields,
	values,
	onApply,
	searchValue,
	onSearchChange,
	onSearchSubmit,
	searchPlaceholder,
	showActiveFilters = false,
	appliedSearch,
}: HistoryFiltersProps): JSX.Element {
	const { t } = useTranslation();
	const fieldIdPrefix = useId();
	const [isOpen, setIsOpen] = useState(false);
	// The tray's working copy — seeded from committed values each time it
	// opens, so Cancel (or dismissing) reliably discards edits. The custom
	// range bounds are always seeded so the From/To inputs stay controlled
	// even for a caller that doesn't pass them (PD-14899).
	const [draft, setDraft] = useState<Record<string, string>>({
		from: '',
		to: '',
		...values,
	});

	useEffect(() => {
		if (isOpen) {
			setDraft({ from: '', to: '', ...values });
		}
	}, [isOpen, values]);

	// Lock body scroll while the tray is open, and close on Escape.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const onKey = (e: KeyboardEvent): void => {
			// PD-15027 — an Escape that closed a dropdown inside the tray
			// arrives handled (the dropdown prevents its default); it closes
			// only that menu, and the next Escape closes the tray.
			if (e.key === 'Escape' && !e.defaultPrevented) {
				setIsOpen(false);
			}
		};
		document.addEventListener('keydown', onKey);
		return (): void => {
			document.removeEventListener('keydown', onKey);
		};
	}, [isOpen]);

	const hasSearch = onSearchChange != null && onSearchSubmit != null;
	const activeCount = countActiveFilters(fields, values, showActiveFilters);
	const chips = showActiveFilters
		? historyFilterChips({
				fields,
				values,
				appliedSearch,
				formatDate: (isoDate) => formatDateSimple(isoDate),
			})
		: [];
	const canClearAll =
		showActiveFilters &&
		historyFiltersDifferFromDefaults(fields, values, appliedSearch);

	// A chip's x (and Clear all) commits straight away, through the same
	// onApply as the tray, so the page maps it to its URL params in ONE update.
	// The search box is the page's own state, so it is emptied alongside.
	const removeChip = (chipId: string): void => {
		if (chipId === HISTORY_SEARCH_CHIP_ID) {
			onSearchChange?.('');
		}
		onApply(historyValuesWithoutChip(chipId, fields, values));
	};

	const clearAllFilters = (): void => {
		onSearchChange?.('');
		onApply(clearedHistoryValues(fields, values));
	};

	const removeChipLabel = (chip: HistoryFilterChipModel): string => {
		if (chip.id === 'range') {
			return t('Show all dates');
		}
		if (chip.id === HISTORY_SEARCH_CHIP_ID) {
			return t('Clear search');
		}
		return t('Remove {{filter}} filter', { filter: t(chip.label) });
	};

	const setDraftValue = (key: string, value: string): void => {
		setDraft((prev) => ({ ...prev, [key]: value }));
	};

	const handleApply = (): void => {
		onApply(draft);
		setIsOpen(false);
	};

	const handleClearAll = (): void => {
		// Every field plus the custom-range bounds (PD-14899), so a later
		// re-pick starts blank, and every committed key the page reads.
		setDraft(blankHistoryValues(fields, values));
	};

	return (
		<>
			<div className="flex items-center gap-2">
				{hasSearch && (
					<form
						className="relative flex-1 min-w-0"
						onSubmit={(e) => {
							e.preventDefault();
							onSearchSubmit();
						}}
					>
						<FontAwesomeIcon
							icon={faMagnifyingGlass}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle text-sm pointer-events-none"
						/>
						<input
							type="search"
							value={searchValue ?? ''}
							onChange={(e) => {
								onSearchChange(e.target.value);
							}}
							placeholder={searchPlaceholder ?? t('Search...')}
							className="search-on-surface w-full pl-9 pr-3 py-2.5 rounded-lg bg-brand-surface border border-brand-border text-sm text-default"
						/>
					</form>
				)}
				{/* Filter button — matches the search box (py-2.5, rounded-lg,
				    same surface/border) so the row reads as one control set. */}
				<button
					type="button"
					onClick={() => {
						setIsOpen(true);
					}}
					className={`${hasSearch ? 'shrink-0' : 'w-full justify-center'} flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2.5 text-sm font-medium text-default dark:text-default`}
					aria-haspopup="dialog"
					aria-expanded={isOpen}
				>
					<FontAwesomeIcon icon={faFilter} className="h-3.5 w-3.5" />
					<span>{t('Filters')}</span>
					{activeCount > 0 && (
						<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-portal-accent text-white text-[11px] font-semibold leading-none">
							{activeCount}
						</span>
					)}
				</button>
			</div>

			{/* Active-filter chips (PD-17321), styled like the catalog's: the
			    date range is always shown, since every history list is
			    limited to some range. */}
			{chips.length > 0 && (
				<div
					role="group"
					aria-label={t('Active filters')}
					className="mt-2 flex flex-wrap items-center gap-2"
				>
					{chips.map((chip) => (
						<span
							key={chip.id}
							className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-portal-accent/10 text-portal-accent text-xs rounded-full"
						>
							<span>
								{t(chip.label)}
								{chip.value != null && (
									<>
										{t(':')} {t(chip.value, chip.params)}
									</>
								)}
							</span>
							{chip.removable && (
								<button
									type="button"
									onClick={() => {
										removeChip(chip.id);
									}}
									className="inline-flex items-center justify-center -mr-1 p-0.5 rounded-full hover:bg-portal-accent/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent"
									aria-label={removeChipLabel(chip)}
								>
									<FontAwesomeIcon icon={faTimes} className="text-[10px]" />
								</button>
							)}
						</span>
					))}
					{canClearAll && (
						<button
							type="button"
							onClick={clearAllFilters}
							className="text-xs text-brand-muted hover:text-strong underline focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent rounded"
						>
							{t('Clear all')}
						</button>
					)}
				</div>
			)}

			{isOpen && (
				<>
					<button
						type="button"
						aria-label={t('Cancel')}
						onClick={() => {
							setIsOpen(false);
						}}
						className="fixed inset-0 z-40 bg-black/40 drawer-backdrop-in"
					/>
					<div
						// Mobile: a bottom sheet docked above the BottomNav — the
						// bottom offset matches its real footprint: 48px tab row +
						// (inset − 12px overlap), floored at 48px for inset-less
						// browsers. Desktop (md+): a regular centered dialog
						// (PD-14899) — the sheet spanning the full viewport width
						// looked broken on wide screens.
						className="fixed inset-x-0 bottom-[max(calc(36px_+_env(safe-area-inset-bottom)),48px)] z-40 rounded-t-2xl bg-brand-surface shadow-2xl flex flex-col overflow-hidden filter-tray-in max-h-[80vh] md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md md:rounded-2xl md:border md:border-brand-border"
						role="dialog"
						aria-modal="true"
						aria-label={t('Filters')}
					>
						<div className="flex items-center justify-between px-4 h-12 border-b border-brand-border shrink-0">
							<span className="text-base font-semibold text-default">
								{t('Filters')}
							</span>
							<button
								type="button"
								onClick={handleClearAll}
								className="text-sm text-portal-accent hover:underline"
							>
								{t('Clear all')}
							</button>
						</div>

						<div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
							{fields.map((field) => (
								<div key={field.key}>
									{/* Toggle rows carry their own inline label, and the
									    location picker renders its own — rendering one here
									    too doubled the "Location" label (PD-14899). */}
									{field.kind !== 'toggle' && field.kind !== 'location' && (
										<label
											id={`${fieldIdPrefix}-${field.key}`}
											className="block text-xs font-medium text-brand-muted mb-1"
										>
											{t(field.label)}
										</label>
									)}
									{field.kind === 'range' ? (
										<>
											{/* PD-15027 — the shared Select, styled like the
											    rest of the storefront, not a native menu. */}
											<Select
												aria-labelledby={`${fieldIdPrefix}-${field.key}`}
												value={
													draft.range === ''
														? (field.defaultRange ?? DEFAULT_HISTORY_RANGE)
														: draft.range
												}
												onChange={(next) => {
													setDraftValue('range', next);
												}}
												options={historyRangeOptions().map((opt) => ({
													value: opt.key,
													label: t(opt.label),
												}))}
											/>
											{/* Explicit from/to bounds for the custom range
											    (PD-14899). Either side may be left blank for an
											    open-ended range; the pickers cross-constrain so
											    "to" can't precede "from". */}
											{draft.range === 'custom' && (
												<div className="mt-2 grid grid-cols-2 gap-2">
													<div>
														<label className="block text-xs font-medium text-brand-muted mb-1">
															{t('From')}
														</label>
														<input
															type="date"
															value={draft.from}
															max={draft.to !== '' ? draft.to : undefined}
															onChange={(e) => {
																setDraftValue('from', e.target.value);
															}}
															className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm"
														/>
													</div>
													<div>
														<label className="block text-xs font-medium text-brand-muted mb-1">
															{t('To')}
														</label>
														<input
															type="date"
															value={draft.to}
															min={draft.from !== '' ? draft.from : undefined}
															onChange={(e) => {
																setDraftValue('to', e.target.value);
															}}
															className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm"
														/>
													</div>
												</div>
											)}
										</>
									) : field.kind === 'toggle' ? (
										<label className="flex items-center gap-2.5 cursor-pointer min-h-touch">
											<input
												type="checkbox"
												checked={draft[field.key] === '1'}
												onChange={(e) => {
													setDraftValue(field.key, e.target.checked ? '1' : '');
												}}
												className="w-4 h-4 text-portal-accent"
											/>
											<span className="text-sm text-default">
												{t(field.label)}
											</span>
										</label>
									) : field.kind === 'location' ? (
										<AccountLocationPicker
											value={draft.clientId ?? ''}
											onChange={(v) => {
												setDraftValue('clientId', v);
											}}
											clients={field.clients}
											className="w-full"
										/>
									) : (
										<Select
											aria-labelledby={`${fieldIdPrefix}-${field.key}`}
											// A key the page never seeded reads as its "All"
											// option, as the native select's first row did.
											value={draft[field.key] ?? ''}
											onChange={(next) => {
												setDraftValue(field.key, next);
											}}
											options={field.options.map((opt) => ({
												value: opt.value,
												label: t(opt.label),
											}))}
										/>
									)}
								</div>
							))}
						</div>

						<div className="flex items-center gap-3 px-4 py-3 border-t border-brand-border shrink-0">
							<button
								type="button"
								onClick={() => {
									setIsOpen(false);
								}}
								className="flex-1 px-4 py-2.5 rounded-lg border border-brand-border bg-brand-surface text-sm font-medium text-default min-h-touch"
							>
								{t('Cancel')}
							</button>
							<button
								type="button"
								onClick={handleApply}
								className="flex-1 px-4 py-2.5 rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold min-h-touch"
							>
								{t('Apply')}
							</button>
						</div>
					</div>
				</>
			)}
		</>
	);
}
